import type { CSSProperties } from "react";
import { AppLink } from "@/shared/components/AppLink";
import { useLocale } from "@/shared/i18n/locale";
import styles from "./CaseShowcaseCard.module.css";
import type { CaseShowcaseCardProps } from "./CaseShowcaseCard.types";
import { useAutoFitCardContent } from "./useAutoFitCardContent";

export function CaseShowcaseCard({
  title,
  tags,
  metrics,
  screenshot,
  screenshotAlt,
  imagePosition,
  href,
  className,
}: CaseShowcaseCardProps) {
  const locale = useLocale();
  const cardClassName = [styles.card, className].filter(Boolean).join(" ");
  const imageStyle = imagePosition
    ? ({
        "--image-position": `${imagePosition.x}% ${imagePosition.y}%`,
        "--image-zoom": String((imagePosition.zoom ?? 100) / 100),
      } as CSSProperties)
    : undefined;

  // Refaz o auto-fit sempre que título ou métricas mudam (troca de case).
  const contentSignature = [
    title,
    ...metrics.map((m) => `${m.prefix ?? ""}${m.value}${m.label}`),
  ].join("|");
  const { containerRef, metricsRef } = useAutoFitCardContent(contentSignature);

  const readCaseText = locale === "en" ? "Read full case" : "Ler case completo";
  const ariaLabel = locale === "en" ? `Read full case: ${title}` : `Ler case completo: ${title}`;

  return (
    <article className={cardClassName} ref={containerRef}>
      <div className={styles.cardBody}>
        <div className={styles.cardIntro}>
          <div className={styles.cardTags}>
            {tags.map((tag) => (
              <span key={tag} className={styles.cardTag}>
                {tag}
              </span>
            ))}
          </div>
          <h3 className={styles.cardTitle} data-card-title>
            {title}
          </h3>
        </div>

        <div className={styles.metrics} ref={metricsRef}>
          {metrics.map((metric) => (
            <div
              key={`${metric.prefix ?? ""}-${metric.value}-${metric.label}`}
              className={styles.metric}
              data-metric
            >
              <span className={styles.metricValue}>
                {metric.prefix && <span className={styles.metricPrefix}>{metric.prefix}</span>}
                {metric.value}
              </span>
              <span className={styles.metricLabel} data-metric-label>
                {metric.label}
              </span>
            </div>
          ))}
        </div>

        {href && (
          <AppLink
            className={styles.link}
            href={href}
            data-track={`Card · ${title}`}
            aria-label={ariaLabel}
          >
            {readCaseText}
          </AppLink>
        )}
      </div>

      <div className={styles.cardMedia}>
        {screenshot ? (
          <img src={screenshot} alt={screenshotAlt || title} loading="lazy" style={imageStyle} />
        ) : (
          <div className={styles.cardMediaFallback} aria-hidden />
        )}
      </div>
    </article>
  );
}
